import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hiddenField", "dropdown"]
  static values = {
    url: String,
    displayFields: Array,
    labelField: String,
    minChars: { type: Number, default: 2 },
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this.createDropdown()
    this.setupEventListeners()
    this.timer = null
  }

  createDropdown() {
    if (!this.hasDropdownTarget) {
      const dropdownDiv = document.createElement("div")
      dropdownDiv.classList.add("smart-search-dropdown")
      dropdownDiv.dataset.smartSearchTarget = "dropdown"
      this.element.appendChild(dropdownDiv)

      // Position the dropdown properly
      this.element.style.position = "relative"
    }

    this.hideDropdown()
  }

  setupEventListeners() {
    this.inputTarget.addEventListener("input", this.handleInput.bind(this))
    this.inputTarget.addEventListener("focus", this.handleFocus.bind(this))
    document.addEventListener("click", this.handleDocumentClick.bind(this))
  }

  handleInput(event) {
    const query = event.target.value.trim()

    // Clear any existing timer
    clearTimeout(this.timer)

    if (query.length < this.minCharsValue) {
      this.hideDropdown()
      return
    }

    // Set a timer to debounce the search
    this.timer = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceValue)
  }

  handleFocus(event) {
    const query = event.target.value.trim()

    if (query.length >= this.minCharsValue) {
      this.performSearch(query)
    }
  }

  performSearch(query) {
    // Show loading indicator
    this.showLoading()

    fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`)
      .then(response => response.json())
      .then(data => {
        this.renderResults(data)
      })
      .catch(error => {
        console.error("Search error:", error)
        this.showError("An error occurred while searching.")
      })
  }

  showLoading() {
    this.dropdownTarget.innerHTML = '<div class="loading-indicator p-3 text-center"><span class="spinner-border spinner-border-sm me-2"></span>Loading...</div>'
    this.showDropdown()
  }

  showError(message) {
    this.dropdownTarget.innerHTML = `<div class="error-message p-3 text-danger">${message}</div>`
    this.showDropdown()
  }

  renderResults(data) {
    const { results, pagination } = data
    this.dropdownTarget.innerHTML = ''

    if (results.length === 0) {
      this.dropdownTarget.innerHTML = '<div class="no-results p-3 text-center text-muted">No results found</div>'
      this.showDropdown()
      return
    }

    // Create results list
    const resultsList = document.createElement("div")
    resultsList.classList.add("results-list")

    results.forEach(item => {
      const resultItem = document.createElement("div")
      resultItem.classList.add("result-item")
      resultItem.innerHTML = this.formatResultItem(item)
      resultItem.dataset.id = item.id
      resultItem.addEventListener("click", () => this.selectItem(item))
      resultsList.appendChild(resultItem)
    })

    this.dropdownTarget.appendChild(resultsList)

    // Add load more button if there are more pages
    if (pagination && pagination.more) {
      const loadMoreBtn = document.createElement("div")
      loadMoreBtn.classList.add("load-more-btn", "text-center", "p-2", "border-top")
      loadMoreBtn.innerHTML = '<button class="btn btn-sm btn-outline-secondary w-100">Load more results</button>'
      loadMoreBtn.addEventListener("click", () => this.loadMoreResults(data.pagination.next_page))
      this.dropdownTarget.appendChild(loadMoreBtn)
    }

    this.showDropdown()
  }

  formatResultItem(item) {
    // Create HTML for displaying the item based on displayFields
    let html = `<div class="result-item-content">`

    // Main title - always use the label field
    html += `<div class="result-title">${item[this.labelFieldValue] || 'Unnamed'}</div>`

    // Only add details section if we have display fields
    if (this.displayFieldsValue.length > 0) {
      html += `<div class="result-details">`

      this.displayFieldsValue.forEach((field, index) => {
        if (item[field] && field !== this.labelFieldValue) {
          if (index > 0) html += ' | '
          html += `<span class="result-detail">${field.charAt(0).toUpperCase() + field.slice(1)}: ${item[field]}</span>`
        }
      })

      html += `</div>`
    }

    html += `</div>`
    return html
  }

  selectItem(item) {
    // Set the input display value to the label field
    this.inputTarget.value = item[this.labelFieldValue]

    // Set the hidden field value to the ID
    this.hiddenFieldTarget.value = item.id

    // Hide the dropdown
    this.hideDropdown()

    // Dispatch a custom event for other components
    this.dispatch('selected', { detail: item })
  }

  loadMoreResults(page) {
    const query = this.inputTarget.value.trim()

    fetch(`${this.urlValue}?q=${encodeURIComponent(query)}&page=${page}`)
      .then(response => response.json())
      .then(data => {
        // Remove the load more button
        const loadMoreBtn = this.dropdownTarget.querySelector('.load-more-btn')
        if (loadMoreBtn) loadMoreBtn.remove()

        // Add new results to the list
        const resultsList = this.dropdownTarget.querySelector('.results-list')

        data.results.forEach(item => {
          const resultItem = document.createElement("div")
          resultItem.classList.add("result-item")
          resultItem.innerHTML = this.formatResultItem(item)
          resultItem.dataset.id = item.id
          resultItem.addEventListener("click", () => this.selectItem(item))
          resultsList.appendChild(resultItem)
        })

        // Add new load more button if needed
        if (data.pagination && data.pagination.more) {
          const newLoadMoreBtn = document.createElement("div")
          newLoadMoreBtn.classList.add("load-more-btn", "text-center", "p-2", "border-top")
          newLoadMoreBtn.innerHTML = '<button class="btn btn-sm btn-outline-secondary w-100">Load more results</button>'
          newLoadMoreBtn.addEventListener("click", () => this.loadMoreResults(data.pagination.next_page))
          this.dropdownTarget.appendChild(newLoadMoreBtn)
        }
      })
      .catch(error => {
        console.error("Load more error:", error)
      })
  }

  showDropdown() {
    this.dropdownTarget.classList.add("show")
  }

  hideDropdown() {
    this.dropdownTarget.classList.remove("show")
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClick.bind(this))
  }
}